/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// pcg-report core: heart-rate-adjusted expected ranges (report-owned clinical bands).
// Source: cardiac_report_v0_5_6_dev_2.html lines 2339-2363 (cardiacCycleRatioZonesForHeartRate,
// expectedSysDiaRangesForHeartRate), 2794-2797 (expectedDiaRangeForHeartRate).
// Deviation: currentHeartRateBpm() global replaced by explicit hr argument at every caller.

import { CARDIAC_CYCLE_RATIO_ZONES } from "./constants"

export function cardiacCycleRatioZonesForHeartRate(hr) {
  if (Number.isFinite(hr)) {
    if (hr < 60) {
      return {
        redLow: 0.25,
        normalLow: 0.33,
        normalHigh: 0.56,
        redHigh: 0.8,
        maxHigh: 1.1,
        label: `Expected SYS:DIA for observed HR (${Math.round(hr)} bpm): 1 : 1.8 to 1 : 3.0`,
      }
    }
    if (hr < 90) {
      return {
        redLow: 0.3,
        normalLow: 0.4,
        normalHigh: 0.67,
        redHigh: 0.95,
        maxHigh: 1.2,
        label: `Expected SYS:DIA for observed HR (${Math.round(hr)} bpm): 1 : 1.5 to 1 : 2.5`,
      }
    }
    if (hr < 120) {
      return {
        redLow: 0.45,
        normalLow: 0.59,
        normalHigh: 1.0,
        redHigh: 1.25,
        maxHigh: 1.45,
        label: `Expected SYS:DIA for observed HR (${Math.round(hr)} bpm): 1 : 1.0 to 1 : 1.7`,
      }
    }
    return {
      redLow: 0.6,
      normalLow: 0.8,
      normalHigh: 1.2,
      redHigh: 1.5,
      maxHigh: 1.8,
      label: `Expected SYS:DIA for observed HR (${Math.round(hr)} bpm): may approach 1 : 1`,
    }
  }
  return { ...CARDIAC_CYCLE_RATIO_ZONES, label: "Expected resting SYS:DIA range: 1 : 1.5 to 1 : 2.5" }
}

export function expectedSysDiaRangesForHeartRate(hr) {
  if (Number.isFinite(hr)) {
    if (hr < 60)
      return {
        sysMin: 280,
        sysMax: 360,
        diaMin: 650,
        diaMax: 900,
        label: `Expected at observed HR (${Math.round(hr)} bpm): SYS 280 – 360 ms · DIA 650 – 900 ms`,
      }
    if (hr < 90)
      return {
        sysMin: 280,
        sysMax: 360,
        diaMin: 450,
        diaMax: 700,
        label: `Expected at observed HR (${Math.round(hr)} bpm): SYS 280 – 360 ms · DIA 450 – 700 ms`,
      }
    if (hr < 120)
      return {
        sysMin: 300,
        sysMax: 380,
        diaMin: 320,
        diaMax: 520,
        label: `Expected at observed HR (${Math.round(hr)} bpm): SYS 300 – 380 ms · DIA 320 – 520 ms`,
      }
    return {
      sysMin: 300,
      sysMax: 400,
      diaMin: 220,
      diaMax: 420,
      label: `Expected at observed HR (${Math.round(hr)} bpm): DIA may approach SYS`,
    }
  }
  return {
    sysMin: 280,
    sysMax: 360,
    diaMin: 450,
    diaMax: 700,
    label: "Expected SYS and DIA durations vary with heart rate",
  }
}

export function expectedDiaRangeForHeartRate(hr) {
  const range = expectedSysDiaRangesForHeartRate(hr)
  return { min: range.diaMin, max: range.diaMax, label: range.label }
}
